<template>
  <div>
    <b-message type="is-warning" has-icon>
      Les changements apportés au cours d'une étape
      <strong>ne seront pas appliqués immédiatement</strong>.
      <br />
      La mise à jour prendra effet une fois que les balises auront récupéré
      <strong>automatiquement</strong> leur configuration (voir estimation ci-dessus). Il est
      possible de <strong>forcer la mise à jour</strong> en resynchronisant manuellement les
      balises, ce qui implique l'envoi de <strong>SMS</strong> (réception non garantie).
    </b-message>

    <b-field>
      <b-checkbox-button
        v-for="option in fieldOptions"
        :key="option.key"
        v-model="visibleFields"
        :native-value="option.key"
        type="is-primary is-light"
      >
        <span>{{ option.label }}</span>
      </b-checkbox-button>
    </b-field>

    <o-table
      v-if="selectedModes.length"
      :items="selectedModes"
      :loading="loading"
      :draggable="false"
      :show-total="false"
    >
      <b-table-column v-slot="{ row }" field="title" label="Mode">
        <b-icon :icon="row.icon" size="is-small" type="is-primary" class="mr-1" />
        <span class="has-text-weight-bold">{{ row.title }}</span>
      </b-table-column>

      <b-table-column v-slot="{ row }" field="intervalField" label="Intervalle (s)" numeric>
        <o-input-number
          lazy
          expanded
          controls-alignment="right"
          controls-position="compact"
          :loading="loading"
          :number.sync="fields[row.intervalField].value"
          :min="fieldInterval[row.key].min"
          :max="fieldInterval[row.key].max"
          :min-error="fieldInterval[row.key].minError"
          :max-error="fieldInterval[row.key].maxError"
          @error="fields[row.intervalField].error = $event"
        />
      </b-table-column>

      <b-table-column v-slot="{ row }" field="pointsField" label="Points par envoi" numeric>
        <o-input-number
          v-if="row.pointsField"
          lazy
          expanded
          controls-alignment="right"
          controls-position="compact"
          :loading="loading"
          :number.sync="fields[row.pointsField].value"
          :min="MIN_POINTS_PER_MESSAGE"
          :max="MAX_POINTS_PER_MESSAGE"
          :min-error="pointsPerMessageError.min"
          :max-error="pointsPerMessageError.max"
          @error="fields[row.pointsField].error = $event"
        />
        <span v-else class="has-text-grey">{{ MIN_POINTS_PER_MESSAGE }}</span>
      </b-table-column>

      <b-table-column v-slot="{ row }" field="sendInterval" label="Envoi" numeric>
        <b-tag v-if="row.sendInterval" type="is-info is-light">{{ row.sendInterval }}</b-tag>
      </b-table-column>
    </o-table>

    <b-field
      v-if="visibleFields.includes('sleepEnabled')"
      label="Activer la veille"
      message="La balise réduit sa fréquence d'envoi lorsqu'elle est immobile. La désactiver augmente la consommation de batterie."
    >
      <b-switch v-model="fields.sleepEnabled.value" type="is-success">
        {{ fields.sleepEnabled.value ? 'Activée' : 'Désactivée' }}
      </b-switch>
    </b-field>

    <hr />

    <b-field>
      <b-button type="is-primary" :loading="loading" :disabled="disabledSubmit" @click="submit">
        Modifier la sélection
      </b-button>
    </b-field>
  </div>
</template>

<script>
import OInputNumber from '@components/InputNumber.vue'
import OTable from '@components/Table.vue'
import { formatDuration } from '@helpers/formatDuration'
import {
  MAX_POINTS_PER_MESSAGE,
  MIN_POINTS_PER_MESSAGE,
} from '@constants/queclinkTrackerConfig/pointsPerMessage'

const MIN_INTERVAL = 1

const MODES = [
  {
    key: 'idle',
    icon: 'moon',
    title: 'Repos',
    intervalField: 'idleIntervalSeconds',
    pointsField: null,
  },
  {
    key: 'default',
    icon: 'location-arrow',
    title: 'Défaut',
    intervalField: 'defaultIntervalSeconds',
    pointsField: 'defaultPointsPerMessage',
  },
  {
    key: 'performance',
    icon: 'tachometer-alt',
    title: 'Performance',
    intervalField: 'performanceIntervalSeconds',
    pointsField: 'performancePointsPerMessage',
  },
]

export default {
  name: 'EditQueclinkTrackerConfigForm',

  components: {
    OInputNumber,
    OTable,
  },

  props: {
    loading: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      MIN_POINTS_PER_MESSAGE,
      MAX_POINTS_PER_MESSAGE,
      visibleFields: [],
      fields: {
        idleIntervalSeconds: {
          value: 120,
          error: null,
          label: 'Intervalle repos',
          show: false,
        },
        defaultIntervalSeconds: {
          value: 60,
          error: null,
          label: 'Intervalle défaut',
          show: false,
        },
        defaultPointsPerMessage: { value: MIN_POINTS_PER_MESSAGE, error: null },
        performanceIntervalSeconds: {
          value: 30,
          error: null,
          label: 'Intervalle performance',
          show: false,
        },
        performancePointsPerMessage: { value: MIN_POINTS_PER_MESSAGE, error: null },
        sleepEnabled: {
          value: true,
          error: null,
          label: 'Veille',
          show: false,
        },
      },
    }
  },

  watch: {
    visibleFields() {
      for (const key in this.fields) {
        this.fields[key].show = this.visibleFields.includes(key)
      }

      for (const mode of MODES) {
        if (this.fields[mode.intervalField].show) {
          continue
        }

        this.fields[mode.intervalField].error = null

        if (mode.pointsField) {
          this.fields[mode.pointsField].error = null
        }
      }
    },
  },

  computed: {
    disabledSubmit() {
      return this.loading || this.hasError || this.visibleFields.length === 0
    },

    hasError() {
      return Object.values(this.fields).some((field) => field.error)
    },

    fieldOptions() {
      return Object.entries(this.fields)
        .filter(([, field]) => field.label)
        .map(([key, field]) => ({ key, label: field.label }))
    },

    selectedModes() {
      return MODES.filter((mode) => this.fields[mode.intervalField].show).map((mode) => ({
        ...mode,
        sendInterval: formatDuration(
          this.fields[mode.intervalField].value *
            (mode.pointsField ? this.fields[mode.pointsField].value : MIN_POINTS_PER_MESSAGE),
        ),
      }))
    },

    fieldInterval() {
      const p = this.fields.performanceIntervalSeconds
      const d = this.fields.defaultIntervalSeconds
      const i = this.fields.idleIntervalSeconds

      return {
        performance: {
          min: MIN_INTERVAL,
          max: d.show ? d.value : i.show ? i.value : undefined,
          minError: `Minimum ${MIN_INTERVAL} seconde.`,
          maxError: d.show
            ? "Doit être inférieur à l'intervalle défaut."
            : "Doit être inférieur à l'intervalle repos.",
        },
        default: {
          min: p.show ? p.value : MIN_INTERVAL,
          max: i.show ? i.value : undefined,
          minError: p.show
            ? "Doit être supérieur à l'intervalle performance."
            : `Minimum ${MIN_INTERVAL} seconde.`,
          maxError: i.show ? "Doit être inférieur à l'intervalle repos." : null,
        },
        idle: {
          min: d.show ? d.value : p.show ? p.value : MIN_INTERVAL,
          max: undefined,
          minError: d.show
            ? "Doit être supérieur à l'intervalle défaut."
            : p.show
            ? "Doit être supérieur à l'intervalle performance."
            : `Minimum ${MIN_INTERVAL} seconde.`,
          maxError: null,
        },
      }
    },

    pointsPerMessageError() {
      return {
        min: `Minimum ${MIN_POINTS_PER_MESSAGE} point.`,
        max: `Maximum ${MAX_POINTS_PER_MESSAGE} points par envoi.`,
      }
    },
  },

  methods: {
    submit() {
      const values = this.visibleFields.reduce((values, key) => {
        values[key] = this.fields[key].value

        return values
      }, {})

      for (const mode of this.selectedModes) {
        if (mode.pointsField) {
          values[mode.pointsField] = this.fields[mode.pointsField].value
        }
      }

      this.$emit('submit', values)
    },
  },
}
</script>
